import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Eine Auswahlliste, in der man tippt statt scrollt.
//
// Entstanden am 13.10.2026 aus der Einheitenauswahl in "Fehlende
// Mietverhältnisse": 110 Einheiten, davon 24 allein in einer Immobilie, viele
// davon Garagen und Stellplätze, die sich nur in der Ziffer unterscheiden.
// Alexander hat sie danach für alle datengetriebenen Listen gewünscht —
// unabhängig von deren Länge, damit sich überall gleich bedienen lässt.
//
// Bewusst ohne Fremdbibliothek. Der erste Anlauf benutzte `command.jsx`
// (cmdk), das im Projekt lag, aber nirgends in Gebrauch war; im Browser blieb
// die Seite leer. Hier läuft nur, was im Projekt überall läuft: ein
// Eingabefeld, eine Liste, ein Knopf.

// Jedes Suchwort muss vorkommen, Reihenfolge egal: "achter gar 3" findet die
// Garage 3 in der Achterstr. 33.
//
// Reine Zahlen treffen nur als GANZES Wort. Ohne diese Regel wäre genau der
// Fall oben unmöglich: Die getippte 3 steckt auch in der Hausnummer 33, und
// "Garage 1" käme mit — die Garage 3 ließe sich nie eindeutig eingrenzen.
// Hausnummer und Einheitsnummer sind beide Zahlen, und eine gesuchte Zahl
// meint die Zahl, nicht einen Teil davon.
export const trefferPasst = (beschriftung, suche) => {
    const text = String(beschriftung || "").toLowerCase();
    const worte = text.split(/[^a-z0-9äöüß]+/).filter(Boolean);
    return String(suche || "").toLowerCase().split(/\s+/).filter(Boolean).every(
        (w) => (/^\d+$/.test(w) ? worte.includes(w) : text.includes(w)));
};

/**
 * @param wert          aktuell gewählter Wert (string)
 * @param onWaehlen     (wert) => void
 * @param optionen      [{ wert, text, gruppe?, hervorgehoben? }]
 * @param platzhalter   Text, solange nichts gewählt ist
 * @param leerText      Beschriftung des Eintrags, der die Auswahl aufhebt;
 *                      fehlt er, lässt sich die Auswahl nicht aufheben
 * @param suchAb        ab wie vielen Optionen das Suchfeld erscheint (Vorgabe 0 = immer)
 */
const SuchAuswahl = ({
    wert, onWaehlen, optionen = [], platzhalter = "— wählen —",
    leerText = null, suchAb = 0, className = "", disabled = false,
    title = undefined, testId = undefined,
}) => {
    const [offen, setOffen] = useState(false);
    const [suche, setSuche] = useState("");
    const [aktiv, setAktiv] = useState(0);
    const huelle = useRef(null);
    const listeRef = useRef(null);

    const mitSuche = optionen.length >= suchAb;
    const gewaehlt = optionen.find((o) => o.wert === wert);
    const treffer = suche ? optionen.filter((o) => trefferPasst(o.text, suche)) : optionen;

    const waehlen = (v) => { onWaehlen(v); setOffen(false); setSuche(""); setAktiv(0); };
    const oeffnen = () => { if (!disabled) { setOffen(true); setSuche(""); setAktiv(0); } };

    // Schließen, wenn woanders hingeklickt wird — sonst verdeckt die offene
    // Liste die Felder darunter.
    useEffect(() => {
        if (!offen) return undefined;
        const beiKlick = (e) => { if (huelle.current && !huelle.current.contains(e.target)) setOffen(false); };
        document.addEventListener("mousedown", beiKlick);
        return () => document.removeEventListener("mousedown", beiKlick);
    }, [offen]);

    // Die mit den Pfeiltasten angesteuerte Zeile in den sichtbaren Bereich
    // holen. Ohne das wandert die Markierung bei langen Listen aus dem Bild.
    useEffect(() => {
        if (!offen || !listeRef.current) return;
        const el = listeRef.current.querySelector(`[data-pos="${aktiv}"]`);
        if (el?.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    }, [aktiv, offen]);

    const beiTaste = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setAktiv((i) => Math.min(i + 1, treffer.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setAktiv((i) => Math.max(i - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); if (treffer[aktiv]) waehlen(treffer[aktiv].wert); }
        else if (e.key === "Escape") { e.preventDefault(); setOffen(false); }
    };

    return (
        <div className={`relative ${className}`} ref={huelle}>
            {!offen || !mitSuche ? (
                <Button type="button" variant="outline" disabled={disabled} title={title}
                        className="h-9 w-full justify-between font-normal"
                        onClick={() => (offen ? setOffen(false) : oeffnen())}
                        data-testid={testId}>
                    <span className={`truncate ${gewaehlt ? "" : "text-gray-500"}`}>
                        {gewaehlt ? gewaehlt.text : platzhalter}
                    </span>
                    <ChevronDown size={14} className="ml-2 shrink-0 opacity-50" />
                </Button>
            ) : (
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input autoFocus value={suche} onKeyDown={beiTaste}
                           onChange={(e) => { setSuche(e.target.value); setAktiv(0); }}
                           placeholder="tippen zum Suchen…" className="h-9 pl-8 pr-8" />
                    <button type="button" onClick={() => setOffen(false)} tabIndex={-1}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                        <X size={13} />
                    </button>
                </div>
            )}

            {offen && (
                <div ref={listeRef}
                     className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {leerText !== null && (
                        <button type="button" onClick={() => waehlen("")}
                                className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 border-b border-gray-100">
                            {leerText}
                        </button>
                    )}
                    {treffer.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center">
                            Nichts passt zu „{suche}“.
                        </div>
                    ) : treffer.map((o, i) => (
                        <button type="button" key={o.wert} data-pos={i}
                                onClick={() => waehlen(o.wert)}
                                onMouseEnter={() => setAktiv(i)}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-1.5
                                    ${i === aktiv ? "bg-gray-100" : "hover:bg-gray-50"}
                                    ${o.wert === wert ? "font-medium" : ""}`}>
                            {o.hervorgehoben && <span className="text-amber-600 shrink-0">★</span>}
                            <span className="truncate">{o.text}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SuchAuswahl;
